<template>
    <div v-if="reservation && metadata.permissions.email_view">
        <grid
            ref="grid"
            class="guest-invoices-grid pt-3"
            overlayNoRowsTemplate="No triggers available"
            :columnDefs="columnDefs"
            :rowData="rowData"
            :headerHeight="45"
            :paginationPageSize="6"
            :pagination="true"
            :isAutoWidth="true"
            :isAutoHeight="true"
            :isSearch="false"
            :with-space="false"
            :autoGroupColumnDef="autoGroupColumnDef"
            :treeData="true"
            :getDataPath="getDataPath"
            :isGroupOpenByDefault="() => true"
        >
        </grid>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../../../api.jsx';
import Grid from '../../../../generic/grid/grid.vue';
import SendTriggersCellRenderer from './send-trigger-cell-renderer.vue';
import { GUEST_TRIGGER_LIST_ENDPOINT, GUEST_TRIGGER_TOGGLE_ENDPOINT } from '../../../../../endpoints';
import { formatResponseError } from '../../../../../utils/api';
import { escapeHTML } from '../../../../../utils/strings';

export default {
    name: 'GuestInvoicesTriggersTab',
    components: { Grid, sendTriggersCellRenderer: SendTriggersCellRenderer },
    inject: ['showSuccessToast', 'showErrorToast'],
    computed: {
        ...mapGetters('guest', ['metadata', 'reservation']),
        columnDefs() {
            return [
                {
                    field: 'timestamps',
                    valueParser: (data) => data.timestamps,
                    headerName: 'Sent',
                    cellRenderer: ({ data }) => {
                        if (!data.id_tech_reservation) {
                            return;
                        }
                        return data?.timestamps?.length > 0
                            ? data.timestamps
                                  .map((name) => {
                                      return escapeHTML(name);
                                  })
                                  .join('<br />')
                            : 'Not Sent';
                    },
                    autoWidthExclude: !this.isMobile,
                    autoHeight: true,
                    sortable: false,
                },
                {
                    field: '',
                    headerName: '',
                    minWidth: 140,
                    cellRenderer: 'sendTriggersCellRenderer',
                    autoWidthExclude: true,
                },
                {
                    field: 'is_active',
                    type: 'switchColumn',
                    headerName: 'Enabled',
                    minWidth: 110,
                    sortable: false,
                    resizable: false,
                    headerClass: 'ag-centered-header',
                    cellClass: 'd-flex justify-content-center',
                    cellRendererParams: ({ data }) => {
                        return {
                            onChange: async ({ data, node, api }, value) => {
                                try {
                                    await API.post(
                                        GUEST_TRIGGER_TOGGLE_ENDPOINT(
                                            this.reservation.user.id,
                                            this.reservation.id,
                                            data.id_trigger
                                        ),
                                        { tech_reservation: data.id_tech_reservation }
                                    );
                                    this.showSuccessToast({
                                        text: `Trigger successfully ${value ? 'enabled' : 'disabled'}.`,
                                    });
                                } catch (e) {
                                    const { generic } = formatResponseError(e);
                                    this.showErrorToast({
                                        text: generic || 'Could not change enabled.',
                                    });
                                    api.redrawRows({ rowNodes: [node] });
                                    return;
                                }
                                await this.fetchTriggers();
                            },
                            disabled: !data?.can_change_is_active,
                        };
                    },
                },
            ];
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
        isTreeData() {
            return this.reservation.properties.length > 1;
        },
        rowData() {
            return this.isTreeData ? this.triggers : this.triggers.filter((item) => !!item.id_tech_reservation);
        },
    },
    mounted() {
        (async () => {
            this.$emit('loading', true);
            try {
                await this.fetchTriggers();
            } finally {
                this.$emit('loading', false);
            }
        })();
    },
    data() {
        return {
            triggers: [],
            autoGroupColumnDef: {
                flex: 2,
                minWidth: 300,
                resizable: true,
                sortable: true,
                sort: 'asc',
                comparator: (valueA, valueB, nodeA, nodeB) => {
                    const nameA = nodeA.data.property_name || nodeA.data.trigger_title;
                    const nameB = nodeB.data.property_name || nodeB.data.trigger_title;
                    return nameA > nameB ? 1 : -1;
                },
                headerName: 'Trigger',
                cellStyle: { textAlign: 'center' },
                cellRendererParams: {
                    innerRenderer: ({ data }) =>
                        escapeHTML(data.id_hierarchy.length === 1 ? data.trigger_title : data.property_name),
                },
            },
        };
    },
    methods: {
        async fetchTriggers() {
            try {
                const { data } = await API.get(
                    GUEST_TRIGGER_LIST_ENDPOINT(this.reservation.user.id, this.reservation.id)
                );
                this.triggers = data;
                console.log(data.filter((item) => !!item.id_tech_reservation));
            } catch (e) {
                this.showErrorToast({ text: 'Could not fetch triggers.' });
            }
        },
        getDataPath(data) {
            return !this.isTreeData ? [data.id] : data.id_hierarchy;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../guest-invoices-grid.scss';
</style>
